import {
  TextAlignCenter,
  TextAlignLeft,
  TextAlignRight,
} from '@phosphor-icons/react'
import TipTapImage from '@tiptap/extension-image'
import {
  type NodeViewProps,
  NodeViewWrapper,
  ReactNodeViewRenderer,
} from '@tiptap/react'
import {
  type CSSProperties,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'

import cn from '@/utils/tailwind/cn'

const useEvent = <T extends (...args: any[]) => any>(handler: T): T => {
  const handlerRef = useRef<T | null>(null)

  useLayoutEffect(() => {
    handlerRef.current = handler
  }, [handler])

  return useCallback((...args: Parameters<T>): ReturnType<T> => {
    if (handlerRef.current === null) {
      throw new Error('Handler is not assigned')
    }
    return handlerRef.current(...args)
  }, []) as T
}

const parseStyleString = (style: string): CSSProperties => {
  if (!style) return {}

  return Object.fromEntries(
    style
      .split(';')
      .map((pair) => pair.split(':').map((s) => s.trim()))
      .filter(([key, value]) => key && value)
      .map(([key, value]) => [
        key.replace(/-([a-z])/g, (_, c) => String(c).toUpperCase()),
        value,
      ])
  )
}

const Divider = () => <div className="h-4 w-[1px] bg-gray-200" />

const MIN_WIDTH = 60
const BORDER_COLOR = '#0096fd'

type AlignType = {
  position: 'left' | 'center' | 'right'
  img: CSSProperties
  container: CSSProperties
}

const ResizableImageTemplate = ({ node, updateAttributes }: NodeViewProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const imgRef = useRef<HTMLImageElement>(null)
  const [editing, setEditing] = useState(false)
  const [resizingStyle, setResizingStyle] = useState<
    Pick<CSSProperties, 'width'> | undefined
  >()
  const [alignStyle, setAlignStyle] = useState<AlignType | undefined>(undefined)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setEditing(false)
      }
    }
    document.addEventListener('click', handleClickOutside)
    return () => {
      document.removeEventListener('click', handleClickOutside)
    }
  }, [editing])

  const handleMouseDown = useEvent(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!imgRef.current) return
      event.preventDefault()
      const direction = event.currentTarget.dataset.direction || '--'
      const initialXPosition = event.clientX
      const currentWidth = imgRef.current.width
      let newWidth = currentWidth
      const transform = direction[1] === 'w' ? -1 : 1

      const removeListeners = () => {
        window.removeEventListener('mousemove', mouseMoveHandler)
        window.removeEventListener('mouseup', removeListeners)
        updateAttributes({ width: newWidth })
        setResizingStyle(undefined)
      }

      const mouseMoveHandler = (event: MouseEvent) => {
        newWidth = Math.max(
          currentWidth + transform * (event.clientX - initialXPosition),
          MIN_WIDTH
        )
        setResizingStyle({ width: newWidth })
        // If mouse is up, remove event listeners
        if (!event.buttons) removeListeners()
      }

      window.addEventListener('mousemove', mouseMoveHandler)
      window.addEventListener('mouseup', removeListeners)
    }
  )

  const dragCornerButton = (direction: string) => (
    <div
      data-direction={direction}
      onMouseDown={handleMouseDown}
      role="button"
      style={{
        position: 'absolute',
        height: '10px',
        width: '10px',
        borderRadius: 2,
        backgroundColor: BORDER_COLOR,
        ...{ n: { top: -3 }, s: { bottom: -3 } }[direction[0]],
        ...{ w: { left: -3 }, e: { right: -3 } }[direction[1]],
        cursor: `${direction}-resize`,
      }}
      tabIndex={0}
    ></div>
  )

  const changeAlign = (type: AlignType['position']) => {
    let imgStyle = undefined
    let containerStyle = undefined

    if (type === 'left') {
      imgStyle = {
        display: 'inline',
        margin: '0px 1em 1em 0px',
        float: 'left',
      }
      containerStyle = {
        float: 'left',
      }
    }
    if (type === 'center') {
      imgStyle = {
        display: 'block',
        margin: 'auto',
      }
      containerStyle = {
        margin: 'auto',
      }
    }
    if (type === 'right') {
      imgStyle = {
        display: 'inline',
        margin: '0px 1em 1em 0px',
        float: 'right',
      }
      containerStyle = {
        float: 'right',
      }
    }

    const styleString = Object.entries(imgStyle || {})
      .map(([key, value]) => `${key}: ${value};`)
      .join(' ')

    updateAttributes({
      style: styleString,
      width: imgRef.current?.width,
    })

    setAlignStyle({
      position: type,
      img: imgStyle as CSSProperties,
      container: containerStyle as CSSProperties,
    })
  }

  const alignList = [
    {
      id: 'left',
      icon: <TextAlignLeft weight="bold" />,
      onClick: () => changeAlign('left'),
    },
    <Divider key={1} />,
    {
      id: 'center',
      icon: <TextAlignCenter weight="bold" />,
      onClick: () => changeAlign('center'),
    },
    <Divider key={2} />,
    {
      id: 'right',
      icon: <TextAlignRight weight="bold" />,
      onClick: () => changeAlign('right'),
    },
  ]

  return (
    <NodeViewWrapper
      data-drag-handle
      draggable
      as="div"
      onBlur={() => setEditing(false)}
      onClick={() => setEditing(true)}
      ref={containerRef}
      style={{
        ...parseStyleString(String(node.attrs.style)),
        position: 'relative',
        display: 'table',
        lineHeight: '0px',
        ...alignStyle?.container,
      }}
    >
      <img
        {...node.attrs}
        alt=""
        ref={imgRef}
        style={{
          ...parseStyleString(String(node.attrs.style)),
          ...resizingStyle,
          ...alignStyle?.img,
          cursor: 'default',
        }}
      />
      {editing && (
        <>
          <div className="absolute -top-4 left-1/2 z-10 flex -translate-x-1/2 items-center rounded border bg-white shadow-sm">
            {alignList.map((item) => {
              if ('id' in item) {
                return (
                  <button
                    className={cn('px-1.5 py-1 text-xl hover:text-primary', {
                      'text-primary': alignStyle?.position === item.id,
                    })}
                    key={item.id}
                    onClick={item.onClick}
                  >
                    {item.icon}
                  </button>
                )
              }
              return item
            })}
          </div>
          <span className="absolute bottom-2 right-2 flex h-6 items-center justify-center rounded bg-primary/5 px-1.5 py-1 text-primary">
            {imgRef.current?.width} x {imgRef.current?.height}
          </span>
          {[
            { left: 0, top: 0, height: '100%', width: '1px' },
            { right: 0, top: 0, height: '100%', width: '1px' },
            { top: 0, left: 0, width: '100%', height: '1px' },
            { bottom: 0, left: 0, width: '100%', height: '1px' },
          ].map((style, i) => (
            <div
              key={i}
              style={{
                position: 'absolute',
                backgroundColor: BORDER_COLOR,
                ...style,
              }}
            ></div>
          ))}
          {dragCornerButton('nw')}
          {dragCornerButton('ne')}
          {dragCornerButton('sw')}
          {dragCornerButton('se')}
        </>
      )}
    </NodeViewWrapper>
  )
}

export const TextEditorResizeImage = TipTapImage.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      src: {
        default: null,
        parseHTML: (element) => element.getAttribute('src'),
        renderHTML: (attributes) => ({ src: attributes.src }),
      },
      style: {
        default: null,
        parseHTML: (element) => element.getAttribute('style'),
        renderHTML: (attributes) => ({ style: attributes.style }),
      },
      alt: {
        default: null,
        parseHTML: (element) => element.getAttribute('alt'),
        renderHTML: (attributes) => ({ alt: attributes.alt }),
      },
      width: {
        default: null,
        parseHTML: (element) => element.getAttribute('width'),
        renderHTML: (attributes) => ({ width: attributes.width }),
      },
      height: {
        default: null,
        parseHTML: (element) => element.getAttribute('height'),
        renderHTML: (attributes) => ({ height: attributes.height }),
      },
    }
  },
  parseHTML() {
    return [
      {
        tag: 'img[src]',
        getAttrs: (dom) => {
          if (typeof dom === 'string') return null
          const element = dom as unknown as HTMLElement
          return {
            src: element.getAttribute('src'),
            alt: element.getAttribute('alt'),
            style: element.getAttribute('style'),
            width: element.getAttribute('width'),
            height: element.getAttribute('height'),
          }
        },
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    return ['img', HTMLAttributes]
  },
  addNodeView() {
    return ReactNodeViewRenderer(ResizableImageTemplate)
  },
}).configure({ inline: true })
